/**
 * MilkRuns - Milk Run Planning & Booking Management
 *
 * Features:
 * - View upcoming and past milk runs
 * - AI-generated suggestions from pending orders
 * - Manage stops, quotes, and brokers
 * - Book runs with selected quotes
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './MilkRuns.css';

// Types
interface MilkRun {
  id: number;
  name: string;
  status: string;
  status_display: string;
  region: string;
  region_display: string;
  planned_date: string;
  departure_time: string | null;
  total_pallets: number;
  total_weight_lbs: number;
  total_miles: number | null;
  estimated_hours: number | null;
  booked_rate: number | null;
  booked_broker_name: string | null;
  stop_count: number;
  capacity_percent: number;
  weight_percent: number;
  is_full: boolean;
  per_pallet_rate: number | null;
  notes: string;
  stops?: MilkRunStop[];
}

interface MilkRunStop {
  id: number;
  customer: number;
  customer_name: string;
  customer_city: string;
  customer_state: string;
  stop_sequence: number;
  pallet_count: number;
  total_weight_lbs: number;
  delivery_address: string;
  delivery_notes: string;
  status: string;
  miles_from_previous: number | null;
}

interface MilkRunSuggestion {
  id: number;
  status: string;
  region: string;
  region_display: string;
  suggested_date: string;
  total_pallets: number;
  total_weight_lbs: number;
  estimated_miles: number | null;
  suggestion_reason: string;
  confidence_score: number;
  customer_count: number;
  customers: { id: number; name: string; city: string }[];
}

interface FreightBroker {
  id: number;
  company: string;
  contact_name: string;
  email: string;
  phone: string;
  is_active: boolean;
  loads_quoted: number;
  loads_booked: number;
  average_rate: number | null;
  win_rate: number;
}

interface Quote {
  id: number;
  broker_id: number;
  broker_company: string;
  broker_name: string;
  broker_phone: string;
  broker_email: string;
  quote_number: string;
  rate: number | null;
  per_pallet: number | null;
  notes: string;
  available: boolean;
  status: string;
  submitted_at: string | null;
  broker_stats: {
    loads_quoted: number;
    loads_booked: number;
    average_rate: number | null;
    win_rate: number;
  };
}

interface Region {
  code: string;
  name: string;
}

interface CapableCustomer {
  id: number;
  name: string;
  city: string;
  state: string;
  has_coordinates: boolean;
  notes: string;
}

type TabType = 'details' | 'stops' | 'quotes';

const STATUS_ICONS: { [key: string]: string } = {
  draft: '\u26AA',           // White circle
  quotes_requested: '\uD83D\uDFE1', // Yellow
  quotes_received: '\uD83D\uDFE1',  // Yellow
  booked: '\uD83D\uDFE2',    // Green
  in_transit: '\uD83D\uDD35', // Blue
  completed: '\u2705',        // Check
  cancelled: '\u274C',        // X
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatCurrency = (amount: number | null) => {
  if (amount === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(amount);
};

const MilkRuns: React.FC = () => {
  // State
  const [upcomingRuns, setUpcomingRuns] = useState<MilkRun[]>([]);
  const [pastRuns, setPastRuns] = useState<MilkRun[]>([]);
  const [suggestions, setSuggestions] = useState<MilkRunSuggestion[]>([]);
  const [brokers, setBrokers] = useState<FreightBroker[]>([]);
  const [brokersCount, setBrokersCount] = useState({ count: 0, max: 10, active_count: 0 });
  const [regions, setRegions] = useState<Region[]>([]);
  const [capableCustomers, setCapableCustomers] = useState<{ [region: string]: { name: string; customers: CapableCustomer[] } }>({});

  const [selectedRun, setSelectedRun] = useState<MilkRun | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [quotesStatus, setQuotesStatus] = useState<any>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPastRuns, setShowPastRuns] = useState(false);
  const [showBrokerModal, setShowBrokerModal] = useState(false);
  const [showAddStopModal, setShowAddStopModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // New run form
  const [newRun, setNewRun] = useState({
    name: '',
    region: '',
    planned_date: '',
    notes: '',
  });

  // New broker form
  const [newBroker, setNewBroker] = useState({
    company: '',
    contact_name: '',
    email: '',
    phone: '',
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [upcomingRes, pastRes, suggestionsRes, brokersRes, regionsRes, customersRes] = await Promise.all([
        api.get('/milk-runs/?status=upcoming'),
        api.get('/milk-runs/?status=past'),
        api.get('/milk-run-suggestions/'),
        api.get('/freight-brokers/'),
        api.get('/milk-runs/regions/'),
        api.get('/milk-runs/capable-customers/'),
      ]);

      setUpcomingRuns(upcomingRes.data);
      setPastRuns(pastRes.data);
      setSuggestions(suggestionsRes.data);
      setBrokers(brokersRes.data.brokers);
      setBrokersCount({
        count: brokersRes.data.count,
        max: brokersRes.data.max,
        active_count: brokersRes.data.active_count,
      });
      setRegions(regionsRes.data);
      setCapableCustomers(customersRes.data);
    } catch (err: any) {
      console.error('Error loading milk run data:', err);
      setError('Failed to load milk run data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load run details
  const loadRunDetails = async (runId: number) => {
    try {
      const res = await api.get(`/milk-runs/${runId}/`);
      setSelectedRun(res.data);

      // Load quotes if run has requested them
      if (['quotes_requested', 'quotes_received', 'booked'].includes(res.data.status)) {
        const quotesRes = await api.get(`/milk-runs/${runId}/quotes/`);
        setQuotes(quotesRes.data.quotes);
        setQuotesStatus(quotesRes.data);
      } else {
        setQuotes([]);
        setQuotesStatus(null);
      }
    } catch (err: any) {
      console.error('Error loading run details:', err);
    }
  };

  // Select a run
  const handleSelectRun = (run: MilkRun) => {
    loadRunDetails(run.id);
    setActiveTab('details');
  };

  // Create new milk run
  const handleCreateRun = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await api.post('/milk-runs/', newRun);
      setShowCreateModal(false);
      setNewRun({ name: '', region: '', planned_date: '', notes: '' });
      await loadData();
      handleSelectRun(res.data);
    } catch (err: any) {
      alert('Failed to create milk run');
    }
  };

  // Accept suggestion
  const handleAcceptSuggestion = async (suggestionId: number) => {
    try {
      const res = await api.post(`/milk-run-suggestions/${suggestionId}/accept/`);
      await loadData();
      if (res.data.milk_run_id) {
        const runRes = await api.get(`/milk-runs/${res.data.milk_run_id}/`);
        handleSelectRun(runRes.data);
      }
    } catch (err: any) {
      alert('Failed to accept suggestion');
    }
  };

  // Decline suggestion
  const handleDeclineSuggestion = async (suggestionId: number) => {
    try {
      await api.post(`/milk-run-suggestions/${suggestionId}/decline/`);
      await loadData();
    } catch (err: any) {
      alert('Failed to decline suggestion');
    }
  };

  // Run detection manually
  const handleDetectNow = async () => {
    try {
      const res = await api.post('/milk-run-suggestions/detect_now/');
      await loadData();
      if (res.data.detected > 0) {
        alert(`Detected ${res.data.detected} new milk run opportunities!`);
      } else {
        alert('No new opportunities found.');
      }
    } catch (err: any) {
      alert('Detection failed');
    }
  };

  // Add stop to run
  const handleAddStop = async (customerId: number, palletCount: number, weightLbs: number) => {
    if (!selectedRun) return;
    try {
      await api.post(`/milk-runs/${selectedRun.id}/add_stop/`, {
        customer_id: customerId,
        pallet_count: palletCount,
        weight_lbs: weightLbs,
      });
      loadRunDetails(selectedRun.id);
      setShowAddStopModal(false);
    } catch (err: any) {
      alert('Failed to add stop');
    }
  };

  // Remove stop
  const handleRemoveStop = async (stopId: number) => {
    if (!selectedRun) return;
    if (!window.confirm('Remove this stop?')) return;
    try {
      await api.post(`/milk-runs/${selectedRun.id}/remove_stop/`, { stop_id: stopId });
      loadRunDetails(selectedRun.id);
    } catch (err: any) {
      alert('Failed to remove stop');
    }
  };

  // Optimize route
  const handleOptimizeRoute = async () => {
    if (!selectedRun) return;
    try {
      await api.post(`/milk-runs/${selectedRun.id}/optimize_route/`);
      loadRunDetails(selectedRun.id);
    } catch (err: any) {
      alert('Failed to optimize route');
    }
  };

  // Request quotes
  const handleRequestQuotes = async () => {
    if (!selectedRun) return;
    if (brokersCount.active_count === 0) {
      alert('No active brokers configured. Add brokers first.');
      return;
    }
    try {
      const res = await api.post(`/milk-runs/${selectedRun.id}/request_quotes/`);
      alert(`Quote requests sent to ${res.data.brokers_contacted} brokers!`);
      loadRunDetails(selectedRun.id);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to request quotes');
    }
  };

  // Book quote
  const handleBookQuote = async (quoteId: number) => {
    if (!selectedRun) return;
    if (!window.confirm('Book this load with the selected quote?')) return;
    try {
      await api.post(`/milk-runs/${selectedRun.id}/book/${quoteId}/`);
      loadRunDetails(selectedRun.id);
      await loadData();
      alert('Load booked! Confirmation email sent to broker.');
    } catch (err: any) {
      alert('Failed to book quote');
    }
  };

  // Create broker
  const handleCreateBroker = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/freight-brokers/', newBroker);
      setNewBroker({ company: '', contact_name: '', email: '', phone: '' });
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to add broker');
    }
  };

  // Toggle broker active
  const handleToggleBroker = async (broker: FreightBroker) => {
    try {
      await api.patch(`/freight-brokers/${broker.id}/`, { is_active: !broker.is_active });
      await loadData();
    } catch (err: any) {
      alert('Failed to update broker');
    }
  };

  if (loading) {
    return <div className="milk-runs-loading">Loading milk run data...</div>;
  }

  return (
    <div className="milk-runs-page">
      {/* Left Panel */}
      <div className="milk-runs-left">
        {/* Upcoming Runs */}
        <section className="runs-section">
          <div className="section-header">
            <h3>Upcoming Runs</h3>
            <button className="btn-small btn-primary" onClick={() => setShowCreateModal(true)}>
              + New Run
            </button>
          </div>
          {upcomingRuns.length === 0 ? (
            <div className="empty-message">No upcoming runs</div>
          ) : (
            <div className="runs-list">
              {upcomingRuns.map(run => (
                <div
                  key={run.id}
                  className={`run-card ${selectedRun?.id === run.id ? 'selected' : ''}`}
                  onClick={() => handleSelectRun(run)}
                >
                  <div className="run-card-header">
                    <span className="status-icon">{STATUS_ICONS[run.status] || '\u26AA'}</span>
                    <span className="run-name">{run.name}</span>
                  </div>
                  <div className="run-card-meta">
                    <span>{formatDate(run.planned_date)}</span>
                    <span>{run.stop_count} stops</span>
                    <span>{run.total_pallets} pallets</span>
                  </div>
                  {run.booked_rate && (
                    <div className="run-card-rate">{formatCurrency(run.booked_rate)}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Past Runs */}
        <section className="runs-section">
          <div className="section-header">
            <h3>Past Runs</h3>
            <button className="btn-text" onClick={() => setShowPastRuns(!showPastRuns)}>
              {showPastRuns ? 'Hide' : 'Show'}
            </button>
          </div>
          {showPastRuns && (
            <div className="runs-list">
              {pastRuns.slice(0, 10).map(run => (
                <div
                  key={run.id}
                  className={`run-card past ${selectedRun?.id === run.id ? 'selected' : ''}`}
                  onClick={() => handleSelectRun(run)}
                >
                  <div className="run-card-header">
                    <span className="status-icon">{STATUS_ICONS[run.status] || '\u26AA'}</span>
                    <span className="run-name">{run.name}</span>
                  </div>
                  <div className="run-card-meta">
                    <span>{formatDate(run.planned_date)}</span>
                    <span>{run.booked_rate ? formatCurrency(run.booked_rate) : '-'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* AI Suggestions */}
        <section className="runs-section">
          <div className="section-header">
            <h3>AI Suggestions</h3>
            <button className="btn-text" onClick={handleDetectNow}>Detect Now</button>
          </div>
          {suggestions.length === 0 ? (
            <div className="empty-message">No pending suggestions</div>
          ) : (
            <div className="suggestions-list">
              {suggestions.map(s => (
                <div key={s.id} className="suggestion-card">
                  <div className="suggestion-header">
                    <span className="suggestion-region">{s.region_display}</span>
                    <span className="suggestion-confidence">{Math.round(s.confidence_score * 100)}%</span>
                  </div>
                  <div className="suggestion-meta">
                    <span>{formatDate(s.suggested_date)}</span>
                    <span>{s.customer_count} customers</span>
                    <span>{s.total_pallets} pallets</span>
                  </div>
                  <div className="suggestion-reason">{s.suggestion_reason}</div>
                  <div className="suggestion-actions">
                    <button className="btn-small btn-success" onClick={() => handleAcceptSuggestion(s.id)}>
                      Accept
                    </button>
                    <button className="btn-small btn-danger" onClick={() => handleDeclineSuggestion(s.id)}>
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Freight Brokers */}
        <section className="runs-section">
          <div className="section-header">
            <h3>Freight Brokers ({brokersCount.count}/{brokersCount.max})</h3>
            <button className="btn-text" onClick={() => setShowBrokerModal(true)}>Manage</button>
          </div>
          <div className="brokers-summary">
            <span>{brokersCount.active_count} active</span>
          </div>
        </section>
      </div>

      {/* Right Panel */}
      <div className="milk-runs-right">
        {!selectedRun ? (
          <div className="no-selection">
            <h2>Select a milk run to view details</h2>
            <p>Or create a new run to get started</p>
          </div>
        ) : (
          <>
            {/* Run Header */}
            <div className="run-detail-header">
              <div className="run-title">
                <span className="status-icon large">{STATUS_ICONS[selectedRun.status] || '\u26AA'}</span>
                <h2>{selectedRun.name}</h2>
                <span className="status-badge">{selectedRun.status_display}</span>
              </div>
              <div className="run-date">{formatDate(selectedRun.planned_date)}</div>
            </div>

            {/* Tabs */}
            <div className="run-tabs">
              <button
                className={`tab ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>
              <button
                className={`tab ${activeTab === 'stops' ? 'active' : ''}`}
                onClick={() => setActiveTab('stops')}
              >
                Stops ({selectedRun.stops?.length || selectedRun.stop_count})
              </button>
              <button
                className={`tab ${activeTab === 'quotes' ? 'active' : ''}`}
                onClick={() => setActiveTab('quotes')}
              >
                Quotes {quotes.length > 0 && `(${quotes.filter(q => q.status === 'submitted').length})`}
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'details' && (
                <div className="details-tab">
                  {/* Capacity Bars */}
                  <div className="capacity-section">
                    <div className="capacity-bar">
                      <label>Pallets: {selectedRun.total_pallets} / 16</label>
                      <div className="bar-bg">
                        <div
                          className={`bar-fill ${selectedRun.capacity_percent >= 90 ? 'full' : ''}`}
                          style={{ width: `${Math.min(selectedRun.capacity_percent, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="capacity-bar">
                      <label>Weight: {selectedRun.total_weight_lbs.toLocaleString()} / 43,000 lbs</label>
                      <div className="bar-bg">
                        <div
                          className={`bar-fill ${selectedRun.weight_percent >= 90 ? 'full' : ''}`}
                          style={{ width: `${Math.min(selectedRun.weight_percent, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Run Info */}
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Region</label>
                      <span>{selectedRun.region_display}</span>
                    </div>
                    <div className="info-item">
                      <label>Est. Miles</label>
                      <span>{selectedRun.total_miles?.toFixed(1) || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>Est. Hours</label>
                      <span>{selectedRun.estimated_hours?.toFixed(1) || '-'}</span>
                    </div>
                    {selectedRun.booked_rate && (
                      <>
                        <div className="info-item">
                          <label>Booked Rate</label>
                          <span className="highlight">{formatCurrency(selectedRun.booked_rate)}</span>
                        </div>
                        <div className="info-item">
                          <label>Per Pallet</label>
                          <span>{formatCurrency(selectedRun.per_pallet_rate)}</span>
                        </div>
                        <div className="info-item">
                          <label>Broker</label>
                          <span>{selectedRun.booked_broker_name}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Notes */}
                  {selectedRun.notes && (
                    <div className="notes-section">
                      <label>Notes</label>
                      <p>{selectedRun.notes}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="detail-actions">
                    {selectedRun.status === 'draft' && selectedRun.stop_count > 0 && (
                      <button className="btn btn-primary" onClick={handleRequestQuotes}>
                        Request Quotes
                      </button>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'stops' && (
                <div className="stops-tab">
                  <div className="stops-header">
                    <button className="btn-small" onClick={() => setShowAddStopModal(true)}>
                      + Add Stop
                    </button>
                    {selectedRun.stops && selectedRun.stops.length > 1 && (
                      <button className="btn-small" onClick={handleOptimizeRoute}>
                        Optimize Route
                      </button>
                    )}
                  </div>

                  {(!selectedRun.stops || selectedRun.stops.length === 0) ? (
                    <div className="empty-message">No stops added yet</div>
                  ) : (
                    <div className="stops-list">
                      {selectedRun.stops.map(stop => (
                        <div key={stop.id} className="stop-card">
                          <div className="stop-sequence">{stop.stop_sequence}</div>
                          <div className="stop-details">
                            <div className="stop-customer">{stop.customer_name}</div>
                            <div className="stop-location">
                              {stop.customer_city}, {stop.customer_state}
                            </div>
                            <div className="stop-meta">
                              {stop.pallet_count} pallets | {stop.total_weight_lbs.toLocaleString()} lbs
                              {stop.miles_from_previous && (
                                <> | {stop.miles_from_previous} mi from prev</>
                              )}
                            </div>
                            {stop.delivery_notes && (
                              <div className="stop-notes">{stop.delivery_notes}</div>
                            )}
                          </div>
                          {selectedRun.status === 'draft' && (
                            <button
                              className="btn-icon btn-danger"
                              onClick={() => handleRemoveStop(stop.id)}
                              title="Remove stop"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'quotes' && (
                <div className="quotes-tab">
                  {!quotesStatus ? (
                    <div className="empty-message">
                      <p>No quotes requested yet.</p>
                      {selectedRun.status === 'draft' && selectedRun.stop_count > 0 && (
                        <button className="btn btn-primary" onClick={handleRequestQuotes}>
                          Request Quotes
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="quotes-summary">
                        <span>{quotesStatus.quotes_received} received</span>
                        <span>{quotesStatus.quotes_pending} pending</span>
                        {quotesStatus.lowest_rate && (
                          <span className="lowest">Lowest: {formatCurrency(quotesStatus.lowest_rate)}</span>
                        )}
                      </div>

                      <div className="quotes-list">
                        {quotes.map(quote => (
                          <div
                            key={quote.id}
                            className={`quote-card ${quote.status === 'accepted' ? 'accepted' : ''} ${!quote.available ? 'unavailable' : ''}`}
                          >
                            <div className="quote-header">
                              <div className="broker-info">
                                <span className="broker-company">{quote.broker_company}</span>
                                <span className="broker-contact">{quote.broker_name}</span>
                              </div>
                              {quote.status === 'accepted' && (
                                <span className="quote-badge accepted">BOOKED</span>
                              )}
                              {quote.status === 'pending' && (
                                <span className="quote-badge pending">Awaiting</span>
                              )}
                            </div>

                            {quote.status === 'submitted' && quote.available && quote.rate && (
                              <>
                                <div className="quote-rate">
                                  <span className="rate-main">{formatCurrency(quote.rate)}</span>
                                  <span className="rate-per-pallet">
                                    {formatCurrency(quote.per_pallet)}/pallet
                                  </span>
                                </div>
                                {quote.notes && <div className="quote-notes">{quote.notes}</div>}
                                <div className="broker-stats">
                                  <span>{quote.broker_stats.loads_booked} loads booked</span>
                                  <span>{quote.broker_stats.win_rate}% win rate</span>
                                  {quote.broker_stats.average_rate && (
                                    <span>Avg: {formatCurrency(quote.broker_stats.average_rate)}</span>
                                  )}
                                </div>
                                {selectedRun.status !== 'booked' && (
                                  <button
                                    className="btn btn-success"
                                    onClick={() => handleBookQuote(quote.id)}
                                  >
                                    Book This Load
                                  </button>
                                )}
                              </>
                            )}

                            {quote.status === 'submitted' && !quote.available && (
                              <div className="quote-unavailable">
                                Broker declined/unavailable
                                {quote.notes && <p>{quote.notes}</p>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Run Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Create New Milk Run</h3>
            <form onSubmit={handleCreateRun}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newRun.name}
                  onChange={e => setNewRun({ ...newRun, name: e.target.value })}
                  placeholder="North Sound Run - Dec 15"
                  required
                />
              </div>
              <div className="form-group">
                <label>Region</label>
                <select
                  value={newRun.region}
                  onChange={e => setNewRun({ ...newRun, region: e.target.value })}
                  required
                >
                  <option value="">Select region...</option>
                  {regions.map(r => (
                    <option key={r.code} value={r.code}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Planned Date</label>
                <input
                  type="date"
                  value={newRun.planned_date}
                  onChange={e => setNewRun({ ...newRun, planned_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newRun.notes}
                  onChange={e => setNewRun({ ...newRun, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create Run</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Broker Modal */}
      {showBrokerModal && (
        <div className="modal-overlay" onClick={() => setShowBrokerModal(false)}>
          <div className="modal broker-modal" onClick={e => e.stopPropagation()}>
            <h3>Manage Freight Brokers ({brokersCount.count}/{brokersCount.max})</h3>

            {brokersCount.count < brokersCount.max && (
              <form onSubmit={handleCreateBroker} className="broker-form">
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="Company"
                    value={newBroker.company}
                    onChange={e => setNewBroker({ ...newBroker, company: e.target.value })}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Contact Name"
                    value={newBroker.contact_name}
                    onChange={e => setNewBroker({ ...newBroker, contact_name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-row">
                  <input
                    type="email"
                    placeholder="Email"
                    value={newBroker.email}
                    onChange={e => setNewBroker({ ...newBroker, email: e.target.value })}
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={newBroker.phone}
                    onChange={e => setNewBroker({ ...newBroker, phone: e.target.value })}
                  />
                </div>
                <button type="submit" className="btn-primary">Add Broker</button>
              </form>
            )}

            <div className="brokers-list">
              {brokers.map(broker => (
                <div key={broker.id} className={`broker-card ${!broker.is_active ? 'inactive' : ''}`}>
                  <div className="broker-info">
                    <span className="broker-company">{broker.company}</span>
                    <span className="broker-contact">{broker.contact_name}</span>
                    <span className="broker-email">{broker.email}</span>
                  </div>
                  <div className="broker-stats">
                    <span>{broker.loads_booked}/{broker.loads_quoted} loads</span>
                    <span>{broker.win_rate}% win</span>
                    {broker.average_rate && <span>Avg: {formatCurrency(broker.average_rate)}</span>}
                  </div>
                  <button
                    className={`btn-small ${broker.is_active ? 'btn-warning' : 'btn-success'}`}
                    onClick={() => handleToggleBroker(broker)}
                  >
                    {broker.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowBrokerModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Stop Modal */}
      {showAddStopModal && selectedRun && (
        <AddStopModal
          run={selectedRun}
          customers={capableCustomers}
          onAdd={handleAddStop}
          onClose={() => setShowAddStopModal(false)}
        />
      )}

      {error && <div className="error-toast">{error}</div>}
    </div>
  );
};

// Add Stop Modal Component
interface AddStopModalProps {
  run: MilkRun;
  customers: { [region: string]: { name: string; customers: CapableCustomer[] } };
  onAdd: (customerId: number, palletCount: number, weightLbs: number) => void;
  onClose: () => void;
}

const AddStopModal: React.FC<AddStopModalProps> = ({ run, customers, onAdd, onClose }) => {
  const [selectedCustomer, setSelectedCustomer] = useState<CapableCustomer | null>(null);
  const [palletCount, setPalletCount] = useState(1);
  const [weightLbs, setWeightLbs] = useState(2000);

  // Get customers in the run's region first
  const regionCustomers = customers[run.region]?.customers || [];
  const otherCustomers = Object.entries(customers)
    .filter(([region]) => region !== run.region)
    .flatMap(([, data]) => data.customers);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCustomer) {
      onAdd(selectedCustomer.id, palletCount, weightLbs);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal add-stop-modal" onClick={e => e.stopPropagation()}>
        <h3>Add Stop to {run.name}</h3>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Customer (from {run.region_display})</label>
            <div className="customer-select-list">
              {regionCustomers.map(c => (
                <div
                  key={c.id}
                  className={`customer-option ${selectedCustomer?.id === c.id ? 'selected' : ''}`}
                  onClick={() => setSelectedCustomer(c)}
                >
                  <span className="customer-name">{c.name}</span>
                  <span className="customer-location">{c.city}, {c.state}</span>
                </div>
              ))}
              {regionCustomers.length === 0 && (
                <div className="empty-message">No milk-run-capable customers in this region</div>
              )}
            </div>
          </div>

          {otherCustomers.length > 0 && (
            <details className="other-regions">
              <summary>Show customers from other regions</summary>
              <div className="customer-select-list">
                {otherCustomers.map(c => (
                  <div
                    key={c.id}
                    className={`customer-option ${selectedCustomer?.id === c.id ? 'selected' : ''}`}
                    onClick={() => setSelectedCustomer(c)}
                  >
                    <span className="customer-name">{c.name}</span>
                    <span className="customer-location">{c.city}, {c.state}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {selectedCustomer && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Pallets</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={palletCount}
                    onChange={e => setPalletCount(e.target.value as any)}
                    onBlur={e => {
                      const val = parseInt(e.target.value) || 1;
                      setPalletCount(Math.min(16, Math.max(1, val)));
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Weight (lbs)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={weightLbs}
                    onChange={e => setWeightLbs(e.target.value as any)}
                    onBlur={e => {
                      const val = parseInt(e.target.value) || 2000;
                      setWeightLbs(Math.max(100, val));
                    }}
                  />
                </div>
              </div>
            </>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={!selectedCustomer}>
              Add Stop
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MilkRuns;
